/**
 * Corrections API Client for Hybrid Extraction System
 * Handles corrections that feed into the learning system
 */
import { apiClient } from './client';

export interface HybridCorrection {
    entity_id: number;
    corrected_value: string;
    field_type: string;
    is_confirmation?: boolean;  // True for positive feedback (thumbs up)
}

export interface CorrectionResponse {
    status: string;
    message: string;
    entity_id: number;
    learning_applied: boolean;
    suggestions_count: number;
}

export interface HybridLearningStats {
    total_corrections: number;
    vendor_aliases_learned: number;
    invoice_formats_learned: number;
    vendors_with_patterns: number;
    last_updated?: string;
}

export interface LearnedPatterns {
    vendor_aliases: Record<string, string>;
    invoice_formats: Record<string, any>;
    vendor_pattern_count: number;
    total_corrections: number;
    last_updated?: string;
    top_formats?: Array<{
        format: string;
        count: number;
        examples: string[];
    }>;
}

export const correctionsApi = {
    /**
     * Submit a correction for hybrid-extracted entity
     */
    async submitHybridCorrection(correction: HybridCorrection): Promise<CorrectionResponse> {
        try {
            const response = await apiClient.post('/api/corrections', correction);
            return response.data;
        } catch (error) {
            console.error('Error submitting hybrid correction:', error);
            throw error;
        }
    },

    /**
     * Get hybrid learning system statistics
     */
    async getHybridLearningStats(): Promise<HybridLearningStats> {
        try {
            const response = await apiClient.get('/api/corrections/stats');
            return response.data;
        } catch (error) {
            console.error('Error fetching hybrid learning stats:', error);
            throw error;
        }
    },

    /**
     * Get learned patterns (vendor aliases, invoice formats, etc.)
     */
    async getLearnedPatterns(): Promise<LearnedPatterns> {
        try {
            const response = await apiClient.get('/api/corrections/patterns');
            return response.data;
        } catch (error) {
            console.error('Error fetching learned patterns:', error);
            throw error;
        }
    },

    /**
     * Health check for corrections API
     */
    async healthCheck(): Promise<any> {
        try {
            const response = await apiClient.get('/api/corrections/health');
            return response.data;
        } catch (error) {
            console.error('Error checking corrections health:', error);
            throw error;
        }
    },

    /**
     * Delete an entity (mark as false positive/irrelevant)
     */
    async deleteEntity(entityId: number): Promise<any> {
        try {
            const response = await apiClient.delete(`/api/corrections/entities/${entityId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting entity:', error);
            throw error;
        }
    }
};

export default correctionsApi;
